import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Moment } from 'moment';

import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ServerApi } from '@app/services/server-api';

import { extractResponseBody } from '@app/lib/utils';
import { PublicCardApplicationRouteManager } from '@app/services/public-card-application-route-manager';
import { moment } from '@app/lib/moment';

export interface IDocument {
  id: string;
  name: string;
  url: string;
  uploadStatus?: string;
  createdAt?: Moment;
  updatedAt?: Moment;
}

export interface IGetAllDocumentsServerResponse {
  documents: Array<IDocumentServerResponseWrapper<IDocumentServerResponse>>;
}

export interface IDocumentServerResponseWrapper<T> {
  document: T;
}

export interface IDocumentServerResponse {
  id: string;
  name: string;
  doc_url: string;
  doc_type: string;
  doc_size: number;
  created_at: string;
  updated_at: string;
  upload_status: string;
  company_card_metadata_id: string;
}

export interface ICreateDocumentRequest {
  name: string;
  doc_size: number;
  doc_type: string;
  public_app?: boolean
}

export interface ICreateDocumentServerResponse {
  id: string;
  name: string;
  doc_type: string;
  doc_size: number;
  doc_url: string;
  created_at: string;
  updated_at: string;
}

export interface ICreateDocumentResponse {
  id: string;
  name: string;
  docType: string;
  docSize: number;
  docUrl: string;
  createdAt: string;
  updatedAt: string;
  doc_url?: string;
  doc_type?: string;
  doc_size?: number;
  created_at?: string;
  updated_at?: string;
  upload_status?: string;
  company_card_metadata_id?: string;
}

export enum DOCUMENT_UPLOAD_STATUS {
  CREATED = 'created',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

@Injectable()
export class DocumentsService {
  constructor(
    public api: ServerApi,
    private http: HttpClient,
    private publicCardApplicationRouteManager: PublicCardApplicationRouteManager,
  ) { }

  public getAll(): Observable<IDocument[]> {
    const params: Map<string, boolean> = new Map();
    if (this.publicCardApplicationRouteManager.isPublicCardApplication()) params.set('public_app', true);
    return this.api.get<IGetAllDocumentsServerResponse>(
      this.getPrefixedURL('w1/documents'),
      params,
    ).pipe(
      map(extractResponseBody),
      map(this.transformGetAll),
    );
  }

  public getById(id: string): Observable<IDocument> {
    const params: Map<string, boolean> = new Map();
    if (this.publicCardApplicationRouteManager.isPublicCardApplication()) params.set('public_app', true);
    return this.api.get<IDocumentServerResponseWrapper<IDocumentServerResponse>>(
      this.getPrefixedURL(`w1/documents/${id}`),
      params,
    ).pipe(
      map(extractResponseBody),
      map(this.transformGetById),
    );
  }

  public post(req: ICreateDocumentRequest): Observable<ICreateDocumentResponse> {
    req = this.publicCardApplicationRouteManager.isPublicCardApplication() ? {...req, public_app: true} : req;
    return this.api.post<IDocumentServerResponseWrapper<ICreateDocumentServerResponse>>(
      this.getPrefixedURL('w1/documents'),
      req,
      null,
    ).pipe(
        map(extractResponseBody),
        map(this.transformPostReq),
      );
  }

  public delete(id: string): Observable<any> {
    const params: Map<string, boolean> = new Map();
    if (this.publicCardApplicationRouteManager.isPublicCardApplication()) params.set('public_app', true);
    return this.api.delete(this.getPrefixedURL(`w1/documents/${id}`), params)
      .pipe(map(extractResponseBody));
  }

  public put(id: number | string, uploadStatus: DOCUMENT_UPLOAD_STATUS, fileSize: number): Observable<IDocument> {
    return this.api.put<IDocumentServerResponseWrapper<IDocumentServerResponse>>(
      this.getPrefixedURL(`w1/documents/${id}`),
      {
        upload_status: uploadStatus,
        doc_size: fileSize,
        ...(this.publicCardApplicationRouteManager.isPublicCardApplication() && {public_app: true}),
      },
    ).pipe(
      map(extractResponseBody),
      map(this.transformPutReq),
    );
  }

  public putDocumentIds(cardApplicationId: string, ids: string[]): Observable<{}> {
    return this.api.put(
      this.getPrefixedURL(`w1/card_applications/${cardApplicationId}`),
      {
        company_card_document_ids: ids,
      },
    ).pipe(
      map(extractResponseBody),
    );
  }

  public uploadfileToS3(fileuploadurl: string, contentType: string, file: File): Observable<HttpEvent<any>> {
    const headers = new HttpHeaders({
      'Content-Type': file.type,
      'Access-Control-Allow-Origin': '*',
    });
    const req = new HttpRequest(
      'PUT',
      fileuploadurl,
      file,
      {
        headers,
        reportProgress: true,
      },
    );
    return this.http.request(req);
  }

  private getPrefixedURL(url: string): string {
    return `fleetcard/${url}`;
  }

  private transformGetAll(data: IGetAllDocumentsServerResponse): IDocument[] {
    return data.documents.map((doc: IDocumentServerResponseWrapper<IDocumentServerResponse>) => {
      return {
        id: doc.document.id,
        name: doc.document.name,
        // waiting for url to be added in our API before mapping it here
        url: doc.document.doc_url,
        uploadStatus: doc.document.upload_status,
        createdAt: moment(doc.document.created_at),
        updatedAt: moment(doc.document.updated_at),
      };
    });
  }

  private transformGetById(data: IDocumentServerResponseWrapper<IDocumentServerResponse>): IDocument {
    const doc = data.document;
    return {
      id: doc.id,
      name: doc.name,
      url: doc.doc_url,
      uploadStatus: doc.upload_status,
      createdAt: moment(doc.created_at),
      updatedAt: moment(doc.updated_at),
    };
  }

  private transformPostReq(data: IDocumentServerResponseWrapper<ICreateDocumentServerResponse>): ICreateDocumentResponse {
    const doc: ICreateDocumentServerResponse = data.document;

    return {
      ...doc,
      docSize: doc.doc_size,
      createdAt: doc.created_at,
      updatedAt: doc.updated_at,
      docUrl: doc.doc_url,
      docType: doc.doc_type,
    };
  }

  private transformPutReq(data: IDocumentServerResponseWrapper<IDocumentServerResponse>): IDocument {
    return {
      id: data.document.id,
      name: data.document.name,
      url: data.document.doc_url,
    };
  }
}
